import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { DrillColorBy, PlanningMode, TimeScale } from '../../planning.types';
import { DatePickerComponent, DatePickerRange } from '../../../date-picker/date-picker.component';

/**
 * Barre de contrôles du planning, sur une seule ligne : sous-onglet, coloration,
 * date, granularité, navigation de période et rapport. Purement presentational.
 */
@Component({
  selector: 'app-planning-controls',
  standalone: true,
  templateUrl: './planning-controls.component.html',
  styleUrls: ['./planning-controls.component.scss'],
  imports: [FormsModule, TranslateModule, SelectComponent, ButtonComponent, DatePickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningControlsComponent {
  readonly mode         = input<PlanningMode>('drilling');
  readonly colorBy      = input<DrillColorBy>('rig');
  readonly timeScale    = input<TimeScale>('week');
  /** Jour de référence de la période affichée (`YYYY-MM-DD`), dont le mois titre le sélecteur de date. */
  readonly date         = input<string>('');
  /** Périodes actives de la vue affichée par le sélecteur de date (un point chacune). */
  readonly markedDates  = input<readonly string[]>([]);
  /** Clé de traduction du premier critère de couleur (« Rig » en forage). */
  readonly primaryColorByLabel = input<string>('PLANNING.RIG');
  readonly reportDisabled      = input<boolean>(true);

  readonly modeChange      = output<PlanningMode>();
  readonly colorByChange   = output<DrillColorBy>();
  readonly timeScaleChange = output<TimeScale>();
  readonly todayClick      = output<void>();
  /** Jour validé dans le sélecteur de date (`YYYY-MM-DD`). */
  readonly datePick        = output<string>();
  /** Période affichée par le sélecteur de date, dont il faut charger les points. */
  readonly visibleRangeChange = output<DatePickerRange>();
  /** -1 pour la période précédente, +1 pour la suivante. */
  readonly navigate        = output<-1 | 1>();
  readonly reportClick     = output<void>();

  private readonly translate = inject(TranslateService);
  /** Recalcule les libellés des listes quand la langue change en cours de session. */
  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  protected readonly colorByOptions = computed<SelectOption[]>(() => {
    this.lang();
    return [
      { id: 'rig',      label: this.translate.instant(this.primaryColorByLabel()) },
      { id: 'operator', label: this.translate.instant('PLANNING.OPERATOR') },
    ];
  });

  protected readonly timeScaleOptions = computed<SelectOption[]>(() => {
    this.lang();
    return [
      { id: 'week',  label: this.translate.instant('PLANNING.WEEK') },
      { id: 'month', label: this.translate.instant('PLANNING.MONTH') },
    ];
  });

  protected onColorBy(option: SelectOption): void {
    this.colorByChange.emit(option.id as DrillColorBy);
  }

  protected onTimeScale(option: SelectOption): void {
    this.timeScaleChange.emit(option.id as TimeScale);
  }

  protected onDatePick(value: string | null): void {
    if (value) { this.datePick.emit(value); }
  }
}
